import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import type { ApplicationSdk } from './core/sdk';
import Navigation from './Navigation';
import { Button } from 'primereact/button';
import { TrainingOverview } from './features/training/manager';
import { OrderOverview } from './features/orders/manager';
import { EmployeeOverview } from './features/employees';
import { ObjectOverview } from './features/objects';
import { ProductOverview } from './features/products';
import { RequestOverview } from './features/requests';
import { ManagerOverview } from './features/managers';
import { NewOrder } from './features/orders/employee';
import { RequestEmployeePage } from './features/requests/employee';
import { TrainingComponent } from './features/training/employee';
import { KihubAdminComponent, KnowledgeOverview } from './features/kihub';
import { SystemSettings } from './admin/system';
import { MenuManagement } from './admin/menues';
import { PermissionManagement } from './admin/permissions';
import IframeView from './components/IframeView';
import DesignManagement from './admin/design/components/DesignManagement';
import EmployeeMenu from './components/EmployeeMenu/EmployeeMenu';
import { MenuHeaderConfig } from './shared/stores/baseEmployeeStore';
import { MATEN_WEBSITE } from './core/constants';
import DocumentAdminView from './features/signabledocuments/components/DocumentAdminView';

export const EmployeeHeaderLogo = ({ sdk }: { sdk: ApplicationSdk }) => {
    const isPremiumDesign: boolean = process.env.REACT_APP_PREMIUM_DESIGN === 'true';
    const useLogo: string | null = sdk.appConfig?.design?.logoUrl || null;

    return isPremiumDesign && useLogo ? (
        <img
            src={useLogo || '/logo_maten.png'}
            alt="Application Logo"
            style={{
                height: '40px',
                objectFit: 'contain',
            }}
        />
    ) : (
        <a
            href={MATEN_WEBSITE}
            target="_blank"
            rel="noopener noreferrer"
        >
            <img
                src="/logo_maten.png"
                alt="Maten Logo"
                style={{
                    height: '40px',
                    objectFit: 'contain',
                }}
            />
        </a>
    );
};

export const EmployeeHeader = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentStore;
    const headerConfig: MenuHeaderConfig | null = store?.menuConfig?.customHeader || null;
    let backButtonVisible: boolean = store?.isBackButtonVisible;

    if (store?.isHeaderHidden === true) {
        return null; // Header nicht anzeigen, wenn isHeaderHidden true ist
    }

    // Visibility of back button could be overwritten by count of menu items
    if (sdk.menuData.length <= 1) {
        backButtonVisible = false;
    }

    return (
        <header
            style={{
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                minHeight: '80px', // Feste Mindesthöhe für konsistente Darstellung
            }}
        >
            {/* Back Button Container - immer da, aber manchmal unsichtbar */}
            <div
                style={{ minWidth: '48px', height: '48px', display: 'flex', alignItems: 'center' }}
            >
                {backButtonVisible ? (
                    <Button
                        icon="pi pi-arrow-left"
                        size="large"
                        outlined
                        style={{
                            fontSize: '1.2rem',
                            color: '#333',
                            padding: '0.75rem',
                            borderRadius: '8px',
                            borderColor: '#ddd',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            minWidth: '48px',
                            height: '48px',
                        }}
                        disabled={store.isBackButtonDisabled}
                        onClick={async () => store.navigateBack()}
                    />
                ) : null}
            </div>
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                }}
            >
                {headerConfig ? (
                    <>
                        {headerConfig.icon && (
                            <i
                                className={headerConfig.icon}
                                style={{ fontSize: '1.5rem', color: '#333' }}
                            ></i>
                        )}
                        {headerConfig.title && (
                            <span style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333' }}>
                                {headerConfig.title}
                            </span>
                        )}
                    </>
                ) : (
                    <EmployeeHeaderLogo sdk={sdk} />
                )}
            </div>

            <div style={{ width: '48px' }}></div>
        </header>
    );
});

export const EmployeeFooter = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentStore;
    const footerConfig = store?.footerButton || null;
    const visible = store?.isFooterButtonVisible ?? false;

    return visible ? (
        <footer
            style={{
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderTop: '1px solid #ddd',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.75rem',
            }}
        >
            {store?.footerButtonHelpText && (
                <div
                    style={{
                        fontSize: '0.9rem',
                        color: '#666',
                        textAlign: 'center',
                        maxWidth: '400px',
                        lineHeight: '1.4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                    }}
                >
                    <i
                        className="pi pi-info-circle"
                        style={{
                            color: '#888',
                            fontSize: '0.9rem',
                        }}
                    ></i>
                    <span>{store.footerButtonHelpText}</span>
                </div>
            )}
            <Button
                label={footerConfig?.label || ''}
                icon={footerConfig?.icon || ''}
                size="large"
                disabled={!store.isFooterButtonEnabled}
                loading={store.isFooterButtonLoading}
                style={{
                    padding: '0.8rem 5rem',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    minWidth: '280px',
                    height: '56px',
                }}
                onClick={() => (footerConfig?.onClick ? footerConfig.onClick() : null)}
            />
        </footer>
    ) : null;
});

// Gemeinsame Layout-Komponente für Employee und Public Views
export const BaseLayout = observer(
    ({
        sdk,
        children,
        showPoweredBy = false,
    }: {
        sdk: ApplicationSdk;
        children: React.ReactNode;
        showPoweredBy?: boolean;
    }) => {
        useEffect(() => {
            // Mobile viewport fix
            const setVH = () => {
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            };

            setVH();
            window.addEventListener('resize', setVH);
            window.addEventListener('orientationchange', setVH);

            return () => {
                window.removeEventListener('resize', setVH);
                window.removeEventListener('orientationchange', setVH);
            };
        }, []);

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(var(--vh, 1vh) * 100)',
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                <EmployeeHeader sdk={sdk} />
                <main
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        padding: '1rem',
                        minHeight: 0,
                        WebkitOverflowScrolling: 'touch', // Smooth scrolling auf iOS
                    }}
                >
                    {children}
                </main>
                <EmployeeFooter sdk={sdk} />
                {showPoweredBy && (
                    <div
                        style={{
                            padding: '0.75rem',
                            backgroundColor: '#fafafa',
                            borderTop: '1px solid #eee',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.75rem',
                            fontSize: '0.85rem',
                            color: '#888',
                            minHeight: '40px',
                        }}
                    >
                        <span>Powered by</span>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                            }}
                        >
                            <img
                                src="/logo_maten.png"
                                alt="Maten"
                                style={{
                                    height: '20px',
                                    objectFit: 'contain',
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

export const EmployeeView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const isPremiumDesign: boolean = process.env.REACT_APP_PREMIUM_DESIGN === 'true';
    const usePremiumLogo: boolean = !!sdk.appConfig?.design?.logoUrl;
    const isMainPage: boolean = sdk.locationAction === 'menu';
    const renderPoweredBy: boolean =
        isMainPage &&
        isPremiumDesign === true &&
        usePremiumLogo &&
        !(sdk.currentStore?.isFooterButtonVisible ?? false);

    return (
        <BaseLayout
            sdk={sdk}
            showPoweredBy={renderPoweredBy}
        >
            {sdk.locationAction === 'menu' ? <EmployeeMenu sdk={sdk} /> : null}
            {sdk.locationAction === 'neworder' ? <NewOrder sdk={sdk} /> : null}
            {sdk.locationAction === 'requests' ? <RequestEmployeePage sdk={sdk} /> : null}
            {sdk.locationAction === 'training' ? <TrainingComponent sdk={sdk} /> : null}
            {sdk.locationAction === 'iframe' && sdk.locationParam ? (
                <IframeView
                    sdk={sdk}
                    menuKey={sdk.locationParam}
                />
            ) : null}
        </BaseLayout>
    );
});

export const PortalView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return (
        <>
            <Navigation sdk={sdk} />
            {sdk.locationEntity === 'manager' ? <ManagerView sdk={sdk} /> : null}
            {sdk.locationEntity === 'admin' ? <AdminView sdk={sdk} /> : null}
        </>
    );
});

const AdminView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return (
        <>
            {sdk.locationAction === 'system' ? <SystemSettings sdk={sdk} /> : null}
            {sdk.locationAction === 'kihub' ? <KihubAdminComponent sdk={sdk} /> : null}
            {sdk.locationAction === 'menu' ? <MenuManagement sdk={sdk} /> : null}
            {sdk.locationAction === 'permissions' ? <PermissionManagement sdk={sdk} /> : null}
            {sdk.locationAction === 'design' ? <DesignManagement sdk={sdk} /> : null}
            {sdk.locationAction === 'documents' ? <DocumentAdminView sdk={sdk} /> : null}
        </>
    );
});

const ManagerView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return (
        <>
            {sdk.locationAction === 'orders' ? <OrderOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'products' ? <ProductOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'objects' ? <ObjectOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'employees' ? <EmployeeOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'managers' ? <ManagerOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'training' ? <TrainingOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'requests' ? <RequestOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'kihub' ? <KnowledgeOverview sdk={sdk} /> : null}
            {sdk.locationAction === 'iframe' && sdk.locationParam ? (
                <IframeView
                    sdk={sdk}
                    menuKey={sdk.locationParam}
                />
            ) : null}
        </>
    );
});
